/**
 * The package's friend doors onto a `Sheet`'s private members.
 *
 * A leaf module on purpose: it has only TYPE edges to `./sheet`, so the row
 * variants, hydrate and the formula engine can value-import it without
 * adding a runtime edge into `sheet.ts` (which value-imports the variants —
 * a cycle that would put `ComputedDataSource` in its TDZ). `Sheet` installs
 * the doors from its static block, once.
 *
 * Four doors, by audience:
 *
 * - `sheetStores` — the per-sheet stores (package-internal).
 * - `sheetInternals` — the other live members the package writes: the shared
 *   canonical cell store, the formula graph, the alias-template overlay, the
 *   backend write coalescer, the promote hook and promote bookkeeping, the
 *   ingestion gate and write revisions an install brackets, and the deferred
 *   cell-listener scope (package-internal).
 * - `sheetOf` — a row's back-pointer to the full `Sheet` it lives in (the
 *   row's `#sheet`). The row's public face is `row.sheetReads` (a
 *   `SheetModelReads`); package code — the row variants included — that
 *   writes through a row's sheet reaches it here (package-internal).
 * - `sheetWiring` — what the SDK layer (`@tento/lona`) wires onto a rows-side
 *   sheet it owns: the ingestion gate, the promote hook, the local cell port
 *   its chokepoint and server-fetch ingest write, the local-only attribute and
 *   parent mirrors of writes the SDK persists itself, and the instrumentation
 *   observers. Nothing else in the SDK reaches a sheet's internals.
 *
 * None of it is on a published entry point. The SDK reaches `sheetWiring`
 * and `sheetOf` through `@tento-lona/sheets/internal-sdk`; this repository's
 * tests reach the doors through `@tento-lona/sheets/internal-testing`. Both
 * are workspace-only (absent from `jsr.json`'s `exports`).
 */

import type { Option } from "../core/option.ts";
import type { SheetNetworkKey } from "../ids/sheet-network-key.ts";
import type { NaiveDate } from "@tento-chrono";
import type { DependencyGraph } from "@tento-core/dependency-graph";
import type { FormulaCoverage } from "../formula/formula-coverage.ts";
import type { AliasExpansionPlan, AliasTemplate } from "../aliases/alias-templates.ts";
import type { IngestionGate } from "../sync/ingestion-gate.ts";
import type { RowWrites } from "../ledger/row-writes.ts";
import type { JsonObject } from "../core/json.ts";
import type { RowLocalId } from "../ids/row-local-id.ts";
import type { BatchRowCells, Unsubscribe } from "../backends/rows-backend.ts";
import type { InstallPacer } from "../core/install-pacer.ts";
import type { AddRowSpec, ModelSheet, SheetLocalCellFeed, SheetStores } from "./rows-sheet.ts";
import type { ReexpandCause } from "./sheet-alias-reexpansion.ts";
import type { SystemRowDef } from "../rows/system-rows.ts";
import type { ModelRow } from "../rows/model-row.ts";
import type { AliasRow } from "../rows/variants/alias-row.ts";
import type { RowEnvelopes } from "../rows/row-envelopes.ts";
import type { CellWriteCoalescer } from "../stores/cell-write-coalescer.ts";
import type { CellWrites } from "../ledger/cell-writes.ts";
import type { SourceRowCellData } from "../stores/source-cell-data.ts";
import type { RowMetadata } from "../stores/row-metadata-store.ts";
import type {
  CellDataStore,
  StoreInstallObserver,
} from "../stores/source-row-cell-data-store.ts";
import type {
  SourceInstallEvent,
  SourceInstallObserver,
  SourceRow,
} from "../rows/variants/source-row.ts";
import { CellId, type SyncKey } from "../ids/ids.ts";
import { RowNetworkKey } from "../ids/row-network-key.ts";

export type { StoreInstallObserver } from "../stores/source-row-cell-data-store.ts";
export type { SourceInstallEvent, SourceInstallObserver } from "../rows/variants/source-row.ts";

/** Observer for a recompute sweep over the sheet's computed sources; gets the swept range. */
export type RecomputeSweepObserver = (range: Option<NaiveDate.Range>) => void;

/** Observer for a drain of the sheet's deferred cell-listener work. */
export type ListenerDrainObserver = () => void;

/**
 * The live members of a `Sheet` the package itself writes, besides the stores.
 * **Package-internal**: the public reads are `Sheet.graph` (a read-only view),
 * `Sheet.peekCanonicalCell` / `subscribeCanonicalCells` and
 * `Sheet.aliasTemplateOverlay`.
 */
/**
 * **A source row's cell writer**: the sheet's cell stores (the shared
 * canonical store, the sheet-local source cells, the row metadata that picks
 * between them), the write revisions an install compares against, the
 * persist coalescer and ledger, the ingestion gate and pacer, the promote
 * hook, and the retirement and listener-deferral switches. Built once by the
 * sheet; a `SourceRow` holds this, never the sheet's internals or stores.
 */
export type SourceRowCells =
  & Pick<
    SheetInternals,
    | "canonicalCells"
    | "cellWriteCoalescer"
    | "cellWrites"
    | "isRetired"
    | "promoteHook"
    | "trackPromotionFlushCell"
    | "ingestionGate"
    | "installPacer"
    | "captureWriteRevisions"
    | "releaseWriteRevisions"
    | "withDeferredListeners"
    | "ensureBatchLoaded"
    | "finishRecompute"
  >
  & Pick<SheetStores, "sourceCellData" | "rowMetadata">;

/** A host's policy for non-fatal request reports; no page or DOM dependency. */
export type RequestFailureReporter = (error: Error, report: () => void) => void;

export interface SheetInternals {
  reportRequestFailure(error: Error, report: () => void): void;
  /** True from the first model disposal, before any owner cleanup or deferred-install wake. */
  isRetired(): boolean;
  /** The client-scoped canonical (`:~`) cell store this sheet shares. */
  readonly canonicalCells: CellDataStore<string>;
  /** Trailing-debounce coalescer for backend cell persists, per `(rowKey, date)`. */
  readonly cellWriteCoalescer: CellWriteCoalescer;
  /** Store-scoped cell write ledger, shared by sheets over the same canonical cells. */
  readonly cellWrites: CellWrites;
  /** The formula dependency graph, writable. */
  readonly graph: DependencyGraph<RowLocalId>;
  /**
   * What each formula row has computed and against which reads — the
   * incremental half of `FormulaEngine.recomputeFormulas`. Disposed with the
   * sheet (it watches the sheet's and the canonical cell stores).
   */
  readonly formulaCoverage: FormulaCoverage;
  /** The per-sheet alias-template overlay (`BuiltinAliasFactory` templates). */
  readonly aliasTemplates: Map<string, AliasTemplate>;
  /** Record consumed signatures only after successful materialization of an attached alias. */
  withAliasExpansion(
    alias: AliasRow,
    operation: (materialize: (plan: AliasExpansionPlan) => void) => void,
  ): void;
  /** The routine a non-empty cell edit on an ephemeral sheet awaits to claim it; `null` when none. */
  promoteHook(): Option<() => Promise<void>>;
  /**
   * Run `release` when the sheet is disposed (`Sheet.dispose`), with the
   * sheet's own subscriptions: for a subscription a builder (hydrate) takes
   * on the sheet's behalf, so a disposed sheet stops receiving fan-out.
   */
  addDisposer(release: () => void): void;
  /**
   * Flip ephemeral→server in place after a promote re-key
   * (`SheetModelCache.rekeyRows`), so prefetch batches against the sheet
   * endpoint. No re-hydrate: the promote stays invisible to the renderer.
   */
  markPromoted(): void;
  /**
   * Record a locally-held cell of an ephemeral sheet (a hydrate seed or a
   * pre-promote edit) so `flushCellsToBackend` persists it after the promote.
   * A no-op once the sheet is server-backed.
   */
  trackPromotionFlushCell(
    rowId: RowLocalId,
    date: NaiveDate.Partial,
    cellId: Option<CellId>,
  ): void;
  /** The installed response-install gate (read side); a waiter awaits `whenReleased()`. */
  readonly ingestionGate: IngestionGate;
  /** The current response-install admission budget, when installed. */
  installPacer(): Option<InstallPacer>;
  /** The local-write revisions an install compares against (see `CellDataStore`), registered on both stores until released. */
  captureWriteRevisions(): SourceRow.WriteRevisions;
  /** The waited snapshot installed or bailed: let both stores prune. */
  releaseWriteRevisions(since: SourceRow.WriteRevisions): void;
  /**
   * Run `fn` with this sheet's own cell-listener side effects (parent
   * recompute, alias re-expansion) deferred to one drain when the outermost
   * call returns. Re-entrant calls run `fn` directly.
   */
  withDeferredListeners<T>(fn: () => T): T;
  /**
   * Stop an inserted row from recomputing its computed parent until the
   * returned release runs: a hydrate builds its tree insert by insert, then
   * recomputes every computed row once over the finished tree. Re-entrant.
   */
  holdInsertRecomputes(): () => void;
  /**
   * The envelopes of `row` (`RowEnvelopes.forRow`), for `ModelRow.envelopes()`:
   * the row module cannot value-import `../rows/row-envelopes` (its chain reaches
   * `class AliasRow extends ModelRow` before `ModelRow` exists), so the
   * sheet — which loads after every variant — answers for it.
   */
  envelopesFor(row: ModelRow): readonly RowEnvelopes.Envelope[];
  /** The one capability a source row of this sheet writes and reads its cells through (`SourceRowCells`). */
  readonly sourceRowCells: SourceRowCells;

  // ── The sheet's own writers (package-internal; tests through
  //    `@tento-lona/sheets/internal-testing`) — none is on the `Sheet` class surface.

  /** Set the title locally; emits `SheetMetadataChanged` (the op runner's `setTitle`). */
  setTitle(title: Option<string>): Promise<void>;
  /** Set the width override locally; emits `SheetWidthChanged` unless unchanged. */
  setWidth(width: Option<number>): Promise<void>;
  /**
   * The client's row-field ledger (`SheetModelCache.rowWrites`), or this
   * sheet's own for a standalone sheet: every write of a row's own fields
   * (label, formula, height, attributes, alias props, fork flags) enters it.
   */
  readonly rowWrites: RowWrites;
  /** The row's own fields as this sheet holds them (`RowWrites.Display.peek`); `null`: not a row of this sheet. */
  peekRowFields(rowId: RowLocalId): Option<RowWrites.Fields>;
  /**
   * Show the ledger's `delta` on this sheet's copy of the row, silently
   * (`RowWrites.Display.show`): metadata, an alias's instance props, a
   * formula's binding and recompute. Answers the extra event fields (an
   * alias's `props.<name>`). A row this sheet does not hold is ignored.
   */
  showRowFields(rowId: RowLocalId, delta: RowWrites.Delta): readonly string[];
  /** The transport of this sheet's row writes: `Backend.updateRow` of the row as keyed when the body is sent. */
  rowTransport(rowId: RowLocalId): RowWrites.Transport;
  /** Merge a display-attribute patch into a row's bag and persist it (row options). */
  setDisplayAttr(
    rowId: RowLocalId,
    patch: Readonly<Record<string, string | number | boolean | null>>,
  ): Promise<void>;
  /** Merge a per-child-preset display patch into a row's `children` bag and persist it. */
  setChildDisplayAttrs(
    rowId: RowLocalId,
    preset: string,
    patch: Readonly<Record<string, string | number | boolean | null>>,
  ): Promise<void>;
  /**
   * Register sync keys a batch response reported pending, each with the row
   * keys whose entries reported it, for a refetch of those rows over `range`.
   */
  watchSyncKeys(
    keys: ReadonlyMap<SyncKey, ReadonlySet<RowNetworkKey>>,
    range: NaiveDate.Range,
  ): void;
  /**
   * The in-flight sheet-wide prefetch that already claims all of `range`, to
   * await before an aggregate fetches for itself; `null` when none does. It
   * requests nothing and settles (never rejects) when that prefetch does:
   * `true` when it delivered (the aggregate counts `range` loaded instead of
   * posting it again), `false` when it failed (the aggregate fetches for
   * itself).
   */
  coveringPrefetch(range: NaiveDate.Range): Option<Promise<boolean>>;
  /**
   * One stored source row's own fetch of `range`, gathered with every other
   * row of this sheet asking for the same range in the same few microtask
   * turns into batches of at most 200 row keys (`SheetPrefetch.gatherRowFetch`):
   * `"installed"` when a batch installed the row, `"alone"` or
   * `"not-served"` when the row fetches for itself; rejects with the cause
   * of the row's batch or its own install. SDK-internal (`SourceRow`'s fetch).
   */
  ensureBatchLoaded(range: NaiveDate.Range, rowKey: RowNetworkKey): Promise<"alone" | "installed" | "not-served">;
  /**
   * Finish row `id`'s recompute now when a budgeted scheduler carried it to a
   * later tick (`SheetRecomputeQueue.finish`), so a read of a computed row never
   * answers its pre-edit cells; a no-op for every other row. `SourceRow`'s reads.
   */
  finishRecompute(id: RowLocalId): void;
  /**
   * One paced `fetchCellsBatch` for every list aggregate that asks for
   * `range` against `gate` in the same tick: the first asker opens the batch,
   * which waits one macrotask for its siblings and then posts the union of
   * their constituents `rows` once, installs every `ok` entry, and answers the
   * whole result (each asker runs its own fallbacks).
   */
  gatherAggregateBatch(
    gate: SheetNetworkKey,
    rows: ReadonlyMap<RowNetworkKey, SourceRow>,
    range: NaiveDate.Range,
  ): Promise<Map<RowNetworkKey, BatchRowCells>>;
  /** `backend.fetchCellsBatch`, paced and retried on a 429. */
  fetchCellsBatchPaced(
    sheetKey: SheetNetworkKey,
    rowKeys: readonly RowNetworkKey[],
    range: NaiveDate.Range,
  ): Promise<Map<RowNetworkKey, BatchRowCells>>;
  /**
   * Re-expand every attached alias of template `name`; `cause` says why
   * (`null`: a hidden input moved — named from the template's declared
   * `reexpandOn` — so every attached alias rebuilds).
   */
  reexpandAliasesByTemplate(name: string, cause: Option<ReexpandCause>): void;
  /** Return a detached alias to its template (drops its forked children). */
  resetAlias(aliasId: RowLocalId): Promise<void>;
  /** Copy-on-write: materialize a source row's default renderer as a real row. */
  ensureRendererMaterialized(sourceId: RowLocalId): Promise<Option<RowLocalId>>;
  /** Undo `ensureRendererMaterialized`: drop the materialized renderer. */
  resetRendererToDefault(sourceId: RowLocalId): Promise<void>;
  /** Add a row under the (server-backed) header surface. */
  addToHeaderSurface(spec: AddRowSpec): Promise<ModelRow>;
  /** Add a row under the (server-backed) footer surface. */
  addToFooterSurface(spec: AddRowSpec): Promise<ModelRow>;
  /** Swap the chrome (system rows) in one coalesced structural change. */
  replaceSystemRows(rows: readonly SystemRowDef[]): void;
  /** Persist an ephemeral sheet's locally-held cells after its promote. */
  flushCellsToBackend(): Promise<void>;
}

/**
 * The SDK layer's door onto a rows-side `Sheet` it owns (see the module note).
 * Reached through `sheetWiring`; application code never holds one.
 */
export interface SheetWiring {
  setRequestFailureReporter(reporter: RequestFailureReporter): void;
  /** Copy this model's resolved graph and waiting references to an SDK cache projection. */
  copyFormulaGraph(destination: DependencyGraph<RowLocalId>): void;
  /** Install the response-install gate; replacing one wakes its waiters. */
  installIngestionGate(gate: IngestionGate): void;
  /** Owner-scoped removal: clears only while `owner` is the installed gate. */
  clearIngestionGate(owner: IngestionGate): void;
  /** Replace the response-install budget, releasing the previous owner. */
  setInstallPacer(pacer: InstallPacer): void;
  /** Owner-scoped removal releases queued installs. */
  clearInstallPacer(owner: InstallPacer): void;
  /** The ingestion slot itself, for an instrumentation seam; do not wire from it. */
  readonly ingestionSlot: IngestionGate.Slot;
  /**
   * Install the routine a non-empty cell edit on an ephemeral sheet awaits to
   * claim it (the owning handle's promote); `null` removes it.
   */
  setPromoteHook(hook: Option<() => Promise<void>>): void;
  /**
   * Make `title` read through `source` — the owning client's sheet list
   * entry (`null` from it: the list does not hold the sheet, the sheet's own
   * title answers) — so a rename the list shows (its update painted,
   * accepted, refused or taken back, a re-read) is the title here too, with
   * no copy to keep in step; `null` removes it.
   */
  setTitleSource(source: Option<() => Option<{ readonly title: Option<string> }>>): void;
  /**
   * The sheet-local (non-canonical) cell store, as the SDK's local-edit
   * chokepoint and server-fetch ingest write it.
   */
  readonly localCells: SourceRowCellData<ModelRow, ModelSheet>;
  /**
   * **Local only** — merge `patch` into the attribute bag of `rowId` (per
   * top-level key) and notify, persisting nothing: a write entered in the
   * row-field ledger and confirmed at once (every display copy shows it,
   * each changed key announced once). A row this sheet does not hold is
   * ignored. A write that should persist goes through `Sheet.setDisplayAttr`.
   */
  patchAttributesLocal(rowId: RowLocalId, patch: JsonObject): void;
  /**
   * The row-field ledger this sheet's rows write through (`RowWrites`): its
   * client's, else its own. The SDK's row facades of a tree no client holds
   * write through it.
   */
  readonly rowWrites: RowWrites;
  /**
   * **Local only** — move `id` under `parent` (`null`: a root), the mirror of
   * a move the owning handle already sent. A no-op when `id` is not in this
   * sheet or already has that parent; sibling order is untouched.
   */
  setParentLocal(id: RowLocalId, parent: Option<RowLocalId>): void;
  /** Observe every batch install on the sheet's source rows. Returns the unsubscribe. */
  observeSourceInstalls(observer: SourceInstallObserver): Unsubscribe;
  /**
   * Observe every server-snapshot install bracket (`asInstall`) on the shared
   * canonical store or the sheet-local store. A returned function runs after
   * the install. Returns the unsubscribe.
   */
  observeStoreInstalls(store: "canonical" | "local", observer: StoreInstallObserver): Unsubscribe;
  /** Observe every recompute sweep over the sheet's computed sources. Returns the unsubscribe. */
  observeRecomputeSweeps(observer: RecomputeSweepObserver): Unsubscribe;
  /** Observe every drain of deferred cell-listener work. Returns the unsubscribe. */
  observeListenerDrains(observer: ListenerDrainObserver): Unsubscribe;
  /** Swap the chrome (system rows) the owning handle computed, in one coalesced structural change. */
  replaceSystemRows(rows: readonly SystemRowDef[]): void;
  /** The sheet-local cell store as a read / subscribe port (see `SheetLocalCellFeed`). */
  localCellFeed(): SheetLocalCellFeed;
  /**
   * A sheet-wide read of `range` going out now (the owning client's bulk
   * read): the rows' own loads inside it wait for it, and each row it
   * answered (`SheetWideRead.cover`) is read over `range` (one read per
   * (row, range)). `settle` it when its install ran, bailed or failed.
   */
  beginSheetRead(range: NaiveDate.Range): SheetWideRead;
  /** Persist an ephemeral sheet's locally-held cells after the owning handle's promote. */
  flushCellsToBackend(): Promise<void>;
}

/** A sheet-wide read in flight on a rows-side sheet (`SheetWiring.beginSheetRead`). */
export interface SheetWideRead {
  /** The read answered `row` over its range and its cells are installed: the row's own loads inside the range issue nothing. */
  cover(row: SourceRow): void;
  /** The read's install ran, bailed or failed: a waiting row it did not cover loads for itself. Idempotent. */
  settle(): void;
}

/** The readers `Sheet`'s static block installs. */
export interface SheetFriendReaders {
  readonly stores: (sheet: ModelSheet) => SheetStores;
  readonly internals: (sheet: ModelSheet) => SheetInternals;
  readonly wiring: (sheet: ModelSheet) => SheetWiring;
}

let readers: Option<SheetFriendReaders> = null;

function installed(): SheetFriendReaders {
  if (readers == null) {
    throw new Error("sheet friend: read before the Sheet class initialised");
  }
  return readers;
}

/**
 * Called once, by `Sheet`'s static block. A second install throws: the doors
 * cannot be re-pointed after the class is initialised.
 */
export function installSheetFriend(install: SheetFriendReaders): void {
  if (readers != null) {
    throw new Error("sheet friend: already installed (only Sheet's static block installs it)");
  }
  readers = install;
}

/** **Package-internal friend accessor** for `sheet`'s stores. */
export function sheetStores(sheet: ModelSheet): SheetStores {
  return installed().stores(sheet);
}

/** **Package-internal friend accessor** for `sheet`'s other live members. */
export function sheetInternals(sheet: ModelSheet): SheetInternals {
  return installed().internals(sheet);
}

/** **The SDK layer's door** onto a rows-side `sheet` it owns — see `SheetWiring`. */
export function sheetWiring(sheet: ModelSheet): SheetWiring {
  return installed().wiring(sheet);
}

let rowSheetReader: Option<(row: ModelRow) => ModelSheet> = null;

/**
 * Called once, by `ModelRow`'s static block. A second install throws: the
 * back-pointer door cannot be re-pointed.
 */
export function installRowFriend(read: (row: ModelRow) => ModelSheet): void {
  if (rowSheetReader != null) {
    throw new Error("row friend: already installed (only ModelRow's static block installs it)");
  }
  rowSheetReader = read;
}

/**
 * **Package-internal friend accessor** for the full `Sheet` a row lives in.
 * The row's public face is `row.sheetReads`, the rows-side reads.
 */
export function sheetOf(row: ModelRow): ModelSheet {
  if (rowSheetReader == null) {
    throw new Error("row friend: read before the ModelRow class initialised");
  }
  return rowSheetReader(row);
}
